import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Card, CardSizesEnum, ServiceIcons } from "@monorepo/components";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { getHistoryEntryPaidDate } from "@monorepo/helpers";
import { useClientQuery } from "@monorepo/services/api/auth";
import theme from "@monorepo/styles/theme";
import { formatDateWithLocale } from "@monorepo/translations/utils";
import { IClient, IFilteredHistoryEntry, IHistoryEntry } from "@monorepo/types";
import { Person } from "@mui/icons-material";
import { Box, Tooltip, Typography } from "@mui/material";
import { styled, SxProps } from "@mui/system";
import { reverse } from "named-urls";

interface IProps {
  entries: Array<IHistoryEntry> | Array<IFilteredHistoryEntry>;
  cartGroupId: string;
  entryDateFormat?: string;
  activeOrderId?: string;
  sx?: SxProps;
  onEntrySelect?: (entry: IHistoryEntry | IFilteredHistoryEntry) => void;
  cartLinkState?: any;
}

const StyledLink = styled(Link)(() => ({
  textDecoration: "none",
  color: "inherit",
}));

interface IHistoryCardProps {
  title: string;
  subtitle?: string;
  sum: string;
  isActive?: boolean;
  icon: ReactNode;
  client?: IClient | null;
}

const HistoryCard: FC<IHistoryCardProps> = ({ title, subtitle, sum, icon, isActive, client }) => {
  const { t } = useTranslation();

  return (
    <Card size={CardSizesEnum.SMALL} active={isActive}>
      <Box
        display="grid"
        gridTemplateColumns={client ? "minmax(2.5rem, 1fr) 10fr 1fr 1fr" : "minmax(2.5rem, 1fr) 10fr 2fr"}
        alignItems="center"
        sx={{
          gridGap: theme.spacing(2),
        }}
      >
        <Box display="flex" justifyContent="center" alignItems="center">
          {icon}
        </Box>
        <Box display="flex" flexDirection="column">
          <Typography>{title}</Typography>
          <Typography color={theme.palette.text.disabled}>{subtitle}</Typography>
        </Box>
        {client && (
          <Tooltip title={t("payment_history.service_paid_by", { name: client.name })} sx={{ justifySelf: "flex-end" }}>
            <Person />
          </Tooltip>
        )}
        <Typography whiteSpace="nowrap" textAlign="right">
          {sum} €
        </Typography>
      </Box>
    </Card>
  );
};

const isEntryTypeBasic = (entry: any): entry is IHistoryEntry => {
  return typeof entry?.service_name === "string";
};

export const HistoryEntriesList: FC<IProps> = ({
  entries,
  cartGroupId,
  entryDateFormat,
  activeOrderId,
  sx,
  onEntrySelect,
  cartLinkState,
}) => {
  const { data: client } = useClientQuery();

  return (
    <Box display="grid" sx={{ gridGap: theme.spacing(2), ...sx }}>
      {entries.map((entry: IHistoryEntry | IFilteredHistoryEntry) => {
        return (
          <StyledLink
            to={{
              pathname: reverse(routeConfig.dashboard.payments.history.cartGroup.order.self, {
                cartGroupId,
                orderId: entry.id,
              }),
              state: cartLinkState,
            }}
            key={entry.id}
            onClick={() => onEntrySelect?.(entry)}
          >
            <HistoryCard
              key={entry.id}
              isActive={activeOrderId ? String(activeOrderId) === String(entry.id) : false}
              icon={<ServiceIcons serviceId={entry.category_id} />}
              title={isEntryTypeBasic(entry) ? entry.service_name : (entry as any).provider_service_name}
              subtitle={formatDateWithLocale(
                new Date(getHistoryEntryPaidDate(entry)),
                entryDateFormat || "MMMM dd 'd.'"
              )}
              sum={isEntryTypeBasic(entry) ? entry.sum : (entry as any).payments_sum}
              client={client?.id !== entry.client?.id.toString() ? entry.client : null}
            />
          </StyledLink>
        );
      })}
    </Box>
  );
};
